import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { AppInputField, AppSelectField, AppTextareaField } from '@/form/FormUI';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import logger from '@/util/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { InAppNotificationDao, createId } from '@rock/db-admin';
import type { EquipmentId, HomeId, InAppNotification, NotificationId, SpaceId, UserId } from '@rock/types';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

const log = logger('SendTestNotification');
const notificationDao = new InAppNotificationDao();

const imageTypeOptions = ['none', 'icon', 'url', 'equipment'] as const;

const NotificationFormSchema = z
    .object({
        title: z.string().min(1, 'Title is required').max(100, 'Title must be 100 characters or less'),
        summary: z.string().min(1, 'Summary is required').max(250, 'Summary must be 250 characters or less'),
        content: z.string().min(1, 'Content is required'),
        imageType: z.enum(imageTypeOptions),
        icon: z.string().optional(),
        imageUrl: z.url().optional(),
        equipmentSpaceId: z.string().optional(),
        equipmentId: z.string().optional(),
    })
    .superRefine((values, ctx) => {
        switch (values.imageType) {
            case 'icon':
                if (!values.icon) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Icon is required when image type is icon',
                        path: ['icon'],
                    });
                }
                break;
            case 'url':
                if (!values.imageUrl) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Image URL is required when image type is url',
                        path: ['imageUrl'],
                    });
                }
                break;
            case 'equipment':
                if (!values.equipmentSpaceId) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Space ID is required when image type is equipment',
                        path: ['equipmentSpaceId'],
                    });
                }
                if (!values.equipmentId) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Equipment ID is required when image type is equipment',
                        path: ['equipmentId'],
                    });
                }
                break;
            default:
                break;
        }
    });

export type NotificationFormValues = z.infer<typeof NotificationFormSchema>;

type SendTestNotificationButtonProps = {
    // The home owner — notifications live in the user's inbox, not the home.
    userId: UserId;
    // The home this test notification is about (stamped as the `homeId` pointer).
    homeId: HomeId;
};

const SendTestNotificationButton: FC<SendTestNotificationButtonProps> = ({ userId, homeId }) => {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const form = useForm({
        resolver: zodResolver(NotificationFormSchema),
        defaultValues: {
            title: '',
            summary: '',
            content: '',
            imageType: 'none',
            icon: '',
            imageUrl: '',
            equipmentSpaceId: '',
            equipmentId: '',
        },
    });

    const imageType = form.watch('imageType');

    const closeDialog = useCallback(() => {
        form.reset();
        setOpen(false);
    }, [form]);

    const onInvalid = useCallback((errors: FieldErrors<NotificationFormValues>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onSubmit = useCallback(
        async (values: NotificationFormValues) => {
            setSubmitting(true);
            try {
                const notificationId = createId<NotificationId>();
                const payload: InAppNotification = {
                    object: 'in-app-notification',
                    schema: 1,
                    id: notificationId,
                    // Test notifications bypass the bus (no `userNotificationId`). `type` only drives
                    // icon-fallback / analytics — not the rendered fields this form supplies — so a
                    // placeholder is fine here.
                    type: 'welcome',
                    createdAt: new Date().toISOString(),
                    title: values.title,
                    summary: values.summary,
                    content: values.content,
                    read: false,
                    readAt: null,
                    archived: false,
                    archivedAt: null,
                    homeId,
                };

                if (values.imageType !== 'none') {
                    switch (values.imageType) {
                        case 'icon':
                            payload.image = {
                                type: 'icon',
                                icon: values.icon!,
                            };
                            break;
                        case 'url':
                            payload.image = {
                                type: 'url',
                                url: values.imageUrl!,
                            };
                            break;
                        case 'equipment':
                            payload.image = {
                                type: 'equipment',
                                homeId,
                                spaceId: values.equipmentSpaceId as SpaceId,
                                equipmentId: values.equipmentId as EquipmentId,
                            };
                            break;
                        default:
                            break;
                    }
                }

                await notificationDao.setNotification(userId, notificationId, payload);

                toast.success('Notification queued', {
                    description: 'A test notification has been created for this home owner.',
                });
                log.info(`Created test notification for user ${userId} (home ${homeId})`);
                closeDialog();
            } catch (err) {
                log.error('Failed to create notification', { err });
                toast.error('Unable to create notification', {
                    description: err instanceof Error ? err.message : 'Unknown error',
                });
            } finally {
                setSubmitting(false);
            }
        },
        [closeDialog, homeId, userId],
    );

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !submitting && setOpen(isOpen)}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">Send Test Notification</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Send test notification</DialogTitle>
                    <DialogDescription>
                        Create a notification to verify delivery to this home. The notification will appear immediately
                        in the app.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-4">
                        <AppInputField
                            control={form.control}
                            name="title"
                            label="Title"
                            placeholder="Water heater tip"
                        />
                        <AppInputField
                            control={form.control}
                            name="summary"
                            label="Summary"
                            placeholder="A short summary that appears in the inbox"
                        />
                        <AppTextareaField
                            control={form.control}
                            name="content"
                            label="Content"
                            placeholder="Write the body of the notification"
                        />
                        <AppSelectField
                            control={form.control}
                            name="imageType"
                            label="Image Type"
                            options={Array.from(imageTypeOptions)}
                            placeholder="Select image type"
                        />
                        {imageType === 'icon' ? (
                            <AppInputField
                                control={form.control}
                                name="icon"
                                label="Icon"
                                placeholder="Lucide Icon Name"
                            />
                        ) : null}
                        {imageType === 'url' ? (
                            <AppInputField
                                control={form.control}
                                name="imageUrl"
                                label="Image URL"
                                placeholder="https://..."
                            />
                        ) : null}
                        {imageType === 'equipment' ? (
                            <>
                                <AppInputField
                                    control={form.control}
                                    name="equipmentSpaceId"
                                    label="Space ID"
                                    placeholder="space-id"
                                />
                                <AppInputField
                                    control={form.control}
                                    name="equipmentId"
                                    label="Equipment ID"
                                    placeholder="equipment-id"
                                />
                            </>
                        ) : null}
                        <DialogFooter className="sm:justify-between">
                            <Button
                                type="button"
                                variant="outline"
                                className="cursor-pointer"
                                disabled={submitting}
                                onClick={closeDialog}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" className="cursor-pointer" disabled={submitting}>
                                {submitting ? 'Sending...' : 'Send notification'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default SendTestNotificationButton;
